/**
 * AdminUsersPage.jsx
 * Path: frontend/src/pages/AdminUsersPage.jsx
 * Description: Prosty panel administracji użytkownikami.
 * Related: api/admin.js
 */

import { useCallback, useContext, useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import { AuthContext } from "../context/AuthContext";
import {
  createAdminUser,
  listAdminUsers,
  resetAdminUserPassword,
  updateAdminUser,
} from "../api/admin";

export default function AdminUsersPage() {
  const { token, user: currentUser } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [temporaryCredential, setTemporaryCredential] = useState(null);
  const [draft, setDraft] = useState({
    email: "",
    name: "",
    role: "worker",
  });

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const rows = await listAdminUsers(token);
      setUsers(rows);
    } catch (err) {
      setError(err?.response?.data?.error || "Nie udało się pobrać użytkowników.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  async function handleCreate(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    setMessage("");
    try {
      await createAdminUser(token, draft);
      setDraft({ email: "", name: "", role: "worker" });
      await loadUsers();
    } catch (err) {
      setError(err?.response?.data?.error || "Nie udało się utworzyć użytkownika.");
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleActive(user) {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      await updateAdminUser(token, user.id, { active: !user.active });
      await loadUsers();
    } catch (err) {
      setError(err?.response?.data?.error || "Nie udało się zmienić statusu użytkownika.");
    } finally {
      setSaving(false);
    }
  }

  async function handleResetPassword(user) {
    const confirmed = window.confirm(
      `Zresetować hasło użytkownika ${user.name} (${user.email})? ` +
      "Wszystkie jego sesje zostaną unieważnione, a hasło jednorazowe będzie pokazane tylko raz."
    );
    if (!confirmed) return;

    setSaving(true);
    setError("");
    setMessage("");
    setTemporaryCredential(null);
    try {
      const result = await resetAdminUserPassword(token, user.id);
      if (!result.temporaryPassword || !result.temporaryPasswordExpiresAt) {
        throw new Error("Serwer nie zwrócił hasła jednorazowego. Wykonaj reset ponownie.");
      }
      setTemporaryCredential({
        userName: user.name,
        email: user.email,
        password: result.temporaryPassword,
        expiresAt: result.temporaryPasswordExpiresAt,
      });
      setMessage(
        `Hasło użytkownika ${user.name} zostało zresetowane. ` +
        "Przekaż mu poniższe hasło jednorazowe."
      );
      await loadUsers();
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          "Nie udało się zresetować hasła użytkownika."
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleCopyTemporaryPassword() {
    if (!temporaryCredential?.password) return;
    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error("Clipboard API unavailable");
      }
      await navigator.clipboard.writeText(temporaryCredential.password);
      setMessage("Hasło jednorazowe zostało skopiowane.");
    } catch (_) {
      setError("Nie udało się skopiować hasła. Zaznacz je i skopiuj ręcznie.");
    }
  }

  return (
    <AppShell
      title="Użytkownicy"
      subtitle="Tworzenie kont i szybkie blokowanie dostępu."
    >
      {error ? <div className="form-error page-error">{error}</div> : null}
      {message ? <div className="form-success page-success" role="status">{message}</div> : null}
      {temporaryCredential ? (
        <section
          className="temporary-password-panel"
          aria-label={`Hasło jednorazowe użytkownika ${temporaryCredential.userName}`}
        >
          <div>
            <strong>Hasło jednorazowe dla {temporaryCredential.userName}</strong>
            <p>
              {temporaryCredential.email}. Skopiuj je teraz — po zamknięciu lub odświeżeniu strony
              nie będzie można wyświetlić go ponownie. Jest ważne do{" "}
              {new Date(temporaryCredential.expiresAt).toLocaleString("pl-PL")} i działa tylko raz.
            </p>
          </div>
          <code data-testid="temporary-password">{temporaryCredential.password}</code>
          <div className="temporary-password-actions">
            <button type="button" className="primary-button" onClick={handleCopyTemporaryPassword}>
              Kopiuj hasło
            </button>
            <button
              type="button"
              className="ghost-button"
              onClick={() => setTemporaryCredential(null)}
            >
              Zamknij
            </button>
          </div>
        </section>
      ) : null}

      <section className="detail-grid">
        <article className="card-section">
          <div className="section-header">
            <div>
              <h2>Nowe konto</h2>
              <p>Użytkownik ustawi hasło przy pierwszym logowaniu.</p>
            </div>
          </div>
          <form className="modal-form" onSubmit={handleCreate}>
            <label>
              <span>Email</span>
              <input
                value={draft.email}
                onChange={(event) => setDraft((current) => ({ ...current, email: event.target.value }))}
                required
              />
            </label>
            <label>
              <span>Nazwa</span>
              <input
                value={draft.name}
                onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))}
                required
              />
            </label>
            <label>
              <span>Rola</span>
              <select
                value={draft.role}
                onChange={(event) => setDraft((current) => ({ ...current, role: event.target.value }))}
              >
                <option value="worker">Worker</option>
                <option value="manager">Manager</option>
                <option value="admin">Admin</option>
              </select>
            </label>
            <button type="submit" className="primary-button" disabled={saving}>
              {saving ? "Zapisywanie..." : "Dodaj użytkownika"}
            </button>
          </form>
        </article>

        <article className="card-section">
          <div className="section-header">
            <div>
              <h2>Lista kont</h2>
              <p>Widok operacyjny dla administracji.</p>
            </div>
          </div>
          {loading ? <div>Ładowanie użytkowników...</div> : null}
          {!loading ? (
            <div className="user-list">
              {users.map((user) => (
                <div key={user.id} className="user-row">
                  <div>
                    <strong>{user.name}</strong>
                    <div className="user-subline">
                      <span>{user.email}</span>
                      <span>{user.role}</span>
                      <span>{user.needsPasswordSet ? "czeka na hasło" : "aktywne hasło"}</span>
                    </div>
                  </div>
                  <div className="user-actions">
                    <button
                      type="button"
                      className="ghost-button danger-button"
                      onClick={() => handleResetPassword(user)}
                      disabled={saving || currentUser?.id === user.id}
                      title={
                        currentUser?.id === user.id
                          ? "Własne hasło zmień w profilu."
                          : undefined
                      }
                    >
                      {currentUser?.id === user.id
                        ? "Własne konto"
                        : user.needsPasswordSet
                          ? "Resetuj ponownie"
                          : "Resetuj hasło"}
                    </button>
                    <button type="button" className="ghost-button" onClick={() => handleToggleActive(user)} disabled={saving}>
                      {user.active ? "Zablokuj" : "Odblokuj"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </article>
      </section>
    </AppShell>
  );
}
